import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { isActiveMembership, requestOrgSwitch, type Membership } from '@/lib/memberships';
import { reloadToHome } from '@/lib/platformAdmin';
import { logger } from '@/lib/logger';

const ERROR_TEXT: Record<string, string> = {
    'not-a-member': 'You are no longer a member of that organization.',
    'inactive-user': 'This account is deactivated.',
    'no-org': 'That organization no longer exists.',
};

/**
 * Multi-org switcher (src/lib/memberships.ts). Renders only for a login with
 * two or more active memberships, and never during a platform-admin view
 * (that bar has its own read-only switcher). A switch reloads the app so
 * every listener reopens in the new org.
 *
 * Sits inside the page header, beside the title that already names the active
 * org, so the select reads "Switch organization" instead of repeating the
 * name; the active org is listed but disabled.
 */
export const OrgSwitcher: React.FC = () => {
    const { user, profile } = useAuth();
    const [memberships, setMemberships] = useState<Membership[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const uid = user?.uid;

    useEffect(() => {
        if (!uid) return;
        return onSnapshot(
            collection(db, 'users', uid, 'memberships'),
            (snap) => setMemberships(snap.docs.map((d) => d.data() as Membership)),
            (err) => logger.error('Memberships listener failed:', err),
        );
    }, [uid]);

    const active = memberships
        .filter(isActiveMembership)
        .sort((a, b) => (a.orgName || a.orgId).localeCompare(b.orgName || b.orgId));
    if (!profile || profile.platformAdminView || active.length < 2) return null;

    const onChange = async (orgId: string) => {
        if (!orgId || orgId === profile.orgId) return;
        setBusy(true);
        setError(null);
        try {
            const outcome = await requestOrgSwitch(orgId);
            if (outcome.status === 'success') {
                reloadToHome();
                return;
            }
            setError(ERROR_TEXT[outcome.errorCode] ?? `Could not switch (${outcome.errorCode}).`);
        } catch (err) {
            logger.error('Org switch failed:', err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div role="region" aria-label="Organization" className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-divider-subtle bg-bg-secondary/60 pl-3 pr-1 py-1 focus-within:ring-2 focus-within:ring-accent-primary/60">
                <Building2 size={14} aria-hidden="true" className="shrink-0 text-text-secondary" />
                <span className="sr-only">Switch organization</span>
                <select
                    disabled={busy}
                    value=""
                    onChange={(e) => onChange(e.target.value)}
                    className="min-w-0 max-w-[16rem] truncate bg-transparent font-semibold text-text-primary pr-1 py-0.5 focus:outline-none disabled:opacity-50"
                >
                    {/* Options take explicit colors: a transparent select leaves the
                        native list white-on-white in the dark theme on some browsers. */}
                    <option value="" disabled className="bg-bg-primary text-text-secondary">Switch organization</option>
                    {active.map((m) => (
                        <option key={m.orgId} value={m.orgId} disabled={m.orgId === profile.orgId} className="bg-bg-primary text-text-primary">
                            {m.orgName || m.orgId}{m.orgId === profile.orgId ? ' (current)' : ''}
                        </option>
                    ))}
                </select>
            </label>
            {busy && <span className="text-text-secondary" role="status">Switching...</span>}
            {error && <span className="text-red-400" role="alert">{error}</span>}
        </div>
    );
};
